
import { debug, Formsy, george, observable, observer, pr, React, RouteComponentProps, theme, ui } from "aaa";
import { ContactsStore } from "stores/ContactsStore";
import { pushRoute } from "stores/RouterFunctions";
import { history } from "stores/RouterStore";
import { IPostContactResponse } from "types/contact";
import { CancelButton } from "../util/CancelButton";
import { NativeRaisedButton } from "./NativeRaisedButton";
import { NewAddressSigning } from "./NewAddressSigning";

const OVERLAY_BACKGROUND = "#777";

@observer
export class NewAddress extends React.Component<RouteComponentProps<{}> & {
    contactsStore: ContactsStore
}, {}> {
    @observable buttonDisabled = false;
    @observable signing = false;
    @observable contactResponse?: IPostContactResponse;

    private originalBackgroundColor: string | null = "white";

    private formFields?: any;

    componentDidMount() {
        window.scrollTo(0, 0);
        this.originalBackgroundColor = document.body.style.backgroundColor;
        document.body.style.backgroundColor = OVERLAY_BACKGROUND;
    }

    componentWillUnmount() {
        document.body.style.backgroundColor = this.originalBackgroundColor ?? "white";
    }

    getCurrentAddress() {
        if (history.location.pathname.endsWith("/update-address")) {
            return this.props.contactsStore.selectedAddress ?? this.props.contactsStore.invalidPrimaryAddress;
        }
    }

    performRequest = async () => {
        if (!this.formFields) {
            return;
        }

        const currentAddress = this.getCurrentAddress();

        try {
            this.buttonDisabled = true;
            this.contactResponse = currentAddress
                ? await this.props.contactsStore.updateAddress(
                    currentAddress,
                    this.formFields.street,
                    this.formFields.streetNumber,
                    this.formFields.zipCode,
                    this.formFields.city,
                    "AT",
                    this.formFields.mainContact ?? false,
                )
                : await this.props.contactsStore.addAddress(
                    this.formFields.street,
                    this.formFields.streetNumber,
                    this.formFields.zipCode,
                    this.formFields.city,
                    "AT",
                    this.formFields.mainContact ?? false,
                );
            this.buttonDisabled = false;
            this.signing = true;
        } catch (err) {
            this.buttonDisabled = false;
            debug.log("### error adding address request", err);
            pushRoute("/error");
        }
    }

    onSubmit = async (fields: any, resetForm: any, invalidateForm: any) => {
        debug.log("### submit", fields);
        this.formFields = fields;
        await this.performRequest();
    }

    onClose = () => {
        // Should this go wrong in some case then try to store history's back route
        // when component mounts, so we can always safely go back
        history.goBack();
    }

    renderModal() {
        if (this.signing) {
            return;
        }

        const currentAddress = this.getCurrentAddress();
        const isMainAddress = currentAddress?.flags?.includes("mainContact");

        return (
            <div className="theme-background-color" style={{ display: "flex", flexDirection: "column" }}>
                <div
                    className="theme-modal-overlay"
                    style={{
                        minHeight: "100vh",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center"
                    }}
                >
                    <div
                        className="theme-paper-color"
                        style={{
                            position: "relative",
                            padding: theme.MARGIN,
                            margin: theme.MARGIN,
                            borderRadius: theme.BORDER_RADIUS,
                            minWidth: 320 - 4 * theme.MARGIN // min width is iPhone SE
                        }}
                    >
                        <pr.A11yRefocus>
                            <CancelButton onClick={this.onClose} showRight color={theme.customColors.georgeLightGrey} />
                        </pr.A11yRefocus>
                        <pr.H1_16_TITLE>{ui.__("address.addNew.title")}</pr.H1_16_TITLE>
                        <br />
                        <Formsy.Form
                            onValidSubmit={this.onSubmit}
                        >
                            <george.SectionTitle>{ui.__("address.street")}</george.SectionTitle >
                            <george.TextFieldFormsy
                                value={currentAddress?.address?.street}
                                name="street"
                                required
                                validationError={ui.__("value.invalid")}
                            />
                            <br />
                            <pr.HFlex style={{ width: "100%" }}>
                                <div style={{ flex: 1 }}>
                                    <george.SectionTitle> {ui.__("address.zipCode")} </george.SectionTitle >
                                    <george.TextFieldFormsy
                                        value={currentAddress?.address?.zipCode}
                                        name="zipCode"
                                        required
                                        validations={{
                                            isNumeric: true,
                                            minLength: 4,
                                        }}
                                        validationError={ui.__("value.invalid")}
                                    />
                                </div>

                                <div style={{ marginLeft: 10, flex: 2 }}>
                                    <george.SectionTitle> {ui.__("address.city")} </george.SectionTitle >
                                    <george.TextFieldFormsy
                                        value={currentAddress?.address?.city}
                                        name="city"
                                        required
                                    />
                                </div>
                            </pr.HFlex>
                            <br />

                            <george.SectionTitle>{ui.__("address.country")}</george.SectionTitle>
                            <pr.P_16_CONTENT>{ui.__("address.country.austria")}</pr.P_16_CONTENT>
                            <br />

                            <george.SectionTitle>
                                {ui.__("contact.main")}{" "}
                                <span style={{ fontSize: 14, fontWeight: "normal", color: theme.customColors.georgeLightGrey }}>{ui.__("common.optional")}</span>
                            </george.SectionTitle >
                            <br />
                            <george.CheckboxFormsy
                                key={isMainAddress} // necessary when site is reloaded
                                disabled={isMainAddress}
                                defaultChecked={isMainAddress}
                                value={isMainAddress}
                                name="mainContact"
                                label={ui.__("address.addNew.option.mainContact")}
                            />
                            <br />

                            <NativeRaisedButton
                                className="theme-action-button-background-color"
                                disabled={this.buttonDisabled}
                                primary
                                fullWidth
                                type="submit"
                                style={{ height: 60, borderRadius: theme.BORDER_RADIUS }}
                                buttonStyle={{ borderRadius: theme.BORDER_RADIUS }}
                                labelStyle={{ fontWeight: "bold", textTransform: "none" }}
                                label={ui.__("common.save")}
                            />
                        </Formsy.Form>
                    </div >
                </div>
            </div>
        );
    }

    renderSigning() {
        if (!this.signing || !this.contactResponse) {
            return;
        }

        return (
            <div
                style={{
                    position: "fixed",
                    marginLeft: "auto",
                    marginRight: "auto",
                    top: 0,
                    zIndex: 100,
                    maxWidth: theme.MAX_APP_WIDTH,
                    width: "100vw",
                    height: "100vh",
                    background: "white"
                }}
            >
                {<NewAddressSigning
                    contactsStore={this.props.contactsStore}
                    contactResponse={this.contactResponse}
                    onClose={() => this.signing = false}
                    onRetry={async () => { await this.performRequest(); }}
                    onSuccess={async () => {
                        try {
                            this.signing = false;

                            // Reload contacts
                            await this.props.contactsStore.load();
                            this.onClose();
                        } catch (err) {
                            debug.log("### error loading contacts after new address");
                            pushRoute("/error");
                        }
                    }}
                />}
            </div>
        );
    }

    render() {
        return (
            <div>
                {this.renderModal()}
                {this.renderSigning()}
            </div>
        );
    }

}




// WEBPACK FOOTER //
// ./src/components/common/NewAddress.tsx